import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Clock, Play, Pause, AlertTriangle, CheckCircle2, Flame, Trophy, Timer, User, ChevronRight, Volume2, VolumeX } from 'lucide-react';
import { getClimbByCompetition, getCompetitionById, getTimerState } from '../services/apiServices';
import { slugToId } from '../util/slug';
import { AudioManager } from './AudioManager';

export const PublicTimer = () => {
  const { shareableId } = useParams();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [competition, setCompetition] = useState(null);
  const [timerState, setTimerState] = useState(null);
  const [localRemainingTime, setLocalRemainingTime] = useState(0);
  const [climbData, setClimbData] = useState([]);
  const [currentRound, setCurrentRound] = useState(1);
  const [allClimbers, setAllClimbers] = useState([]);
  const [currentRoundClimbers, setCurrentRoundClimbers] = useState([]);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [showAudioPrompt, setShowAudioPrompt] = useState(true);
  
  // Refs
  const isMountedRef = useRef(true);
  const timerIntervalRef = useRef(null);
  const animationFrameRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const scrollContainerRef = useRef(null);
  
  // Handle user interaction for audio
  const handleUserInteraction = () => {
    setShowAudioPrompt(false);
  };
  
  // Stop local timer
  const stopLocalTimer = useCallback(() => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
  }, []);
  
  // Compute remaining time from end time
  const computeRemainingFromEndTime = useCallback((timer) => {
    if (!timer?.currentEndTime) return 0;
    
    const now = Date.now();
    const end = new Date(timer.currentEndTime).getTime();
    
    const diffInMs = end - now;
    const diffInSeconds = Math.max(0, Math.ceil(diffInMs / 1000));
    
    return diffInSeconds;
  }, []);
  
  // Start local countdown timer
  const startLocalTimer = useCallback((currentTimerState) => {
    stopLocalTimer();
    
    let lastUpdate = Date.now();
    
    timerIntervalRef.current = setInterval(() => {
      const now = Date.now();
      const elapsed = now - lastUpdate;
      
      // Only update if at least 900ms passed (prevents double updates)
      if (elapsed >= 900) {
        lastUpdate = now;
        
        setLocalRemainingTime(prevTime => {
          // Calculate remaining time from currentEndTime if available
          if (currentTimerState?.currentEndTime) {
            const computedRemaining = computeRemainingFromEndTime(currentTimerState);
            if (computedRemaining >= 0) {
              return computedRemaining;
            }
          }
          
          // Fallback: decrement by 1 second
          const newTime = Math.max(0, prevTime - 1);
          return newTime;
        });
      }
    }, 1000);
  }, [stopLocalTimer, computeRemainingFromEndTime]);
  
  // Fetch competition data
  useEffect(() => {
    async function fetchCompetition() {
      try {
        const competitionId = slugToId(shareableId);
        if (!competitionId) return;
        
        const response = await getCompetitionById(competitionId);
        if (!response || response === "false") return;
        
        const competitionData = await response.json();
        setCompetition(competitionData);
        
        if (competitionData.competitors) {
          setAllClimbers(competitionData.competitors);
        }
      } catch (error) {
        console.error("Error fetching competition:", error);
      }
    }
    
    if (shareableId) {
      fetchCompetition();
    }
  }, [shareableId]);
  
  // Fetch timer state - main polling function
  const fetchTimerState = useCallback(async () => {
    try {
      const competitionId = slugToId(shareableId);
      if (!competitionId) return;
      
      const response = await getTimerState(competitionId);
      if (response && response !== "false") {
        const data = await response.json();
        if (data.success && data.data) {
          const timerData = data.data;
          
          // Update last sync time
          setLastSyncTime(new Date().toISOString());
          
          // Update timer state
          setTimerState(timerData);
          
          // Update current round
          if (timerData.currentRound) {
            setCurrentRound(timerData.currentRound);
          }
          
          // Calculate remaining time
          const computedRemaining = computeRemainingFromEndTime(timerData);
          if (computedRemaining > 0) {
            setLocalRemainingTime(computedRemaining);
          } else if (timerData.remainingTime && timerData.remainingTime > 0) {
            setLocalRemainingTime(timerData.remainingTime);
          }
          
          // Manage local timer
          const { status, currentPhase } = timerData;
          
          stopLocalTimer();
          
          if (timerData.currentEndTime &&
            (status === 'running' ||
              ['countdown', 'climbing', 'wrap_time', 'warning'].includes(currentPhase))) {
            startLocalTimer(timerData);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching timer state:', error);
    }
  }, [shareableId, stopLocalTimer, startLocalTimer, computeRemainingFromEndTime]);
  
  // Fetch climb data
  const fetchClimbData = useCallback(async () => {
    try {
      const competitionId = slugToId(shareableId);
      const response = await getClimbByCompetition(competitionId);
      if (response && response !== "false") {
        const data = await response.json();
        setClimbData(data);
      }
    } catch (error) {
      console.error("Error fetching climb data:", error);
    }
  }, [shareableId]);
  
  // Setup polling intervals
  useEffect(() => {
    isMountedRef.current = true;
    
    // Initial fetch
    if (shareableId) {
      fetchTimerState();
      fetchClimbData();
    }
    
    // Set up polling intervals
    const timerPolling = setInterval(() => {
      if (isMountedRef.current) {
        fetchTimerState();
      }
    }, 3000);
    
    const climbPolling = setInterval(() => {
      if (isMountedRef.current) {
        fetchClimbData();
      }
    }, 5000);
    
    pollingIntervalRef.current = {
      timer: timerPolling,
      climb: climbPolling
    };
    
    return () => {
      isMountedRef.current = false;
      stopLocalTimer();
      
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current.timer);
        clearInterval(pollingIntervalRef.current.climb);
      }
    };
  }, [shareableId, fetchTimerState, fetchClimbData, stopLocalTimer]);
  
  // Update current round climbers
  useEffect(() => {
    const updateCurrentRoundClimbers = () => {
      if (!competition || !climbData.length || !timerState) {
        setCurrentRoundClimbers([]);
        return;
      }
      
      const currentRoundToShow = timerState.currentRound || currentRound || 1;
      const boulders = competition.boulders || [];
      const climbers = [];
      
      boulders.forEach(boulder => {
        const climbsForBoulder = climbData.filter(climb =>
          climb.boulderID === boulder._id &&
          climb.round === currentRoundToShow
        );
        
        const climb = climbsForBoulder[0];
        if (climb && climb.climberID) {
          climbers.push({
            boulder: boulder.name,
            climber: climb.climberID.name || 'Unknown',
            bib: climb.climberID.bib || '?',
            boulderColor: boulder.color || '#666'
          });
        }
      });
      
      setCurrentRoundClimbers(climbers);
    };
    
    updateCurrentRoundClimbers();
  }, [competition, climbData, timerState, currentRound]);
  
  // Update current time every second
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  
  // Smooth horizontal scrolling for climbers
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer || currentRoundClimbers.length === 0) return;
    
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    
    let scrollPosition = 0;
    const scrollSpeed = 30;
    let lastTimestamp = 0;
    
    const scroll = (timestamp) => {
      if (!lastTimestamp) lastTimestamp = timestamp;
      const elapsed = (timestamp - lastTimestamp) / 1000;
      lastTimestamp = timestamp;
      
      scrollPosition += scrollSpeed * elapsed;
      
      const singleSetWidth = scrollContainer.scrollWidth / 2;
      if (scrollPosition >= singleSetWidth) {
        scrollPosition = 0;
      }
      
      scrollContainer.scrollLeft = scrollPosition;
      animationFrameRef.current = requestAnimationFrame(scroll);
    };
    
    animationFrameRef.current = requestAnimationFrame(scroll);
    
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [currentRoundClimbers]);
  
  // Format time for display
  const formatTime = (seconds) => {
    if (!seconds && seconds !== 0) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Calculate score
  const calculateScore = useCallback((event) => {
    if (!event) return 0;
    
    let zonePoints = 0;
    let topPoints = 0;
    const Zone = competition?.zonepoints || 10;
    const Top = competition?.toppoints || 25;
    const penalty = competition?.penalty || 0.1;
    
    if (event.zoneReached && event.zoneAttempt) {
      zonePoints = Zone - (event.zoneAttempt - 1) * penalty;
    }
    
    if (event.topReached && event.topAttempt) {
      topPoints = Top - (event.topAttempt - 1) * penalty;
    }
    
    return Math.max(zonePoints, topPoints);
  }, [competition?.zonepoints, competition?.toppoints, competition?.penalty]);
  
  // Get last round results
  const getLastRoundResults = useCallback(() => {
    if (!competition || !climbData.length || !timerState) return [];
    
    const roundToShow = timerState.currentRound || currentRound || 1;
    const boulders = competition.boulders || [];
    const results = [];
    
    boulders.forEach(boulder => {
      const climbsForBoulder = climbData.filter(climb =>
        climb.boulderID === boulder._id &&
        climb.round == roundToShow
      );
      
      const climb = climbsForBoulder[0];
      if (climb && climb.climberID) {
        results.push({
          boulder: boulder.name,
          climber: climb.climberID.name || 'Unknown',
          bib: climb.climberID.bib || '?',
          attemptsToTop: climb.topReached ? climb.topAttempt : null,
          attemptsToZone: climb.zoneReached ? climb.zoneAttempt : null,
          attempts: climb.attempts || 0,
          topReached: climb.topReached || false,
          zoneReached: climb.zoneReached || false,
          points: calculateScore(climb)
        });
      } else {
        results.push({
          boulder: boulder.name,
          climber: '—',
          bib: '—',
          attemptsToTop: null,
          attemptsToZone: null,
          attempts: 0,
          topReached: false,
          zoneReached: false,
          points: 0
        });
      }
    });
    
    return results;
  }, [competition, climbData, calculateScore, timerState, currentRound]);
  
  // Get next round climbers
  const getNextRoundClimbers = useCallback(() => {
    if (!competition || !allClimbers.length || !timerState) return [];
    
    const nextRound = (timerState.currentRound || currentRound || 1) + 1;
    const totalRounds = timerState.totalRounds || competition.totalRounds || 1;
    
    if (nextRound > totalRounds) return [];
    
    const boulders = competition.boulders || [];
    const nextRoundClimbers = [];
    
    boulders.forEach(boulder => {
      const climbsForBoulder = climbData.filter(climb =>
        climb.boulderID === boulder._id &&
        climb.round === nextRound
      );
      
      const climb = climbsForBoulder[0];
      if (climb && climb.climberID) {
        nextRoundClimbers.push({
          boulder: boulder.name,
          climber: climb.climberID.name || 'Unknown',
          bib: climb.climberID.bib || '?'
        });
      } else {
        nextRoundClimbers.push({
          boulder: boulder.name,
          climber: '—',
          bib: '—'
        });
      }
    });
    
    return nextRoundClimbers;
  }, [competition, climbData, allClimbers, timerState, currentRound]);
  
  // Get timer display info
  const getTimerDisplayInfo = () => {
    if (!timerState || !timerState.status) {
      return {
        bgGradient: 'from-slate-900 via-slate-800 to-slate-900',
        textColor: 'text-white',
        accentColor: 'text-slate-300',
        label: 'READY',
        showResults: false,
        showCurrentClimber: false,
        showNextRound: false,
        showCurrentRoundClimbers: false
      };
    }
    
    const { currentPhase, status } = timerState;
    const warningTime = competition?.warningTime || 60;
    const phase = status || currentPhase;
    
    switch (phase) {
      case 'countdown':
        return {
          bgGradient: 'from-slate-900 via-slate-800 to-slate-900',
          textColor: 'text-white',
          accentColor: 'text-slate-300',
          label: 'OBSERVATION TIME',
          showResults: false,
          showCurrentClimber: false,
          showNextRound: false,
          showCurrentRoundClimbers: true
        };
      case 'climbing':
        if (localRemainingTime <= warningTime) {
          return {
            bgGradient: 'from-slate-900 via-slate-800 to-slate-900',
            textColor: 'text-yellow-300',
            accentColor: 'text-slate-300',
            label: 'CLIMB TIME — WARNING',
            showResults: false,
            showCurrentClimber: false,
            showNextRound: false,
            showCurrentRoundClimbers: true
          };
        }
        return {
          bgGradient: 'from-slate-900 via-slate-800 to-slate-900',
          textColor: 'text-white',
          accentColor: 'text-slate-300',
          label: 'CLIMB TIME',
          showResults: false,
          showCurrentClimber: false,
          showNextRound: false,
          showCurrentRoundClimbers: true
        };
      case 'wrap_time':
        return {
          bgGradient: 'from-slate-900 via-slate-800 to-slate-900',
          textColor: 'text-yellow-300',
          accentColor: 'text-slate-300',
          label: 'WRAPPING — NEXT ROTATION STARTS IN',
          showResults: true,
          showCurrentClimber: false,
          showNextRound: true,
          showCurrentRoundClimbers: false
        };
      case 'warning':
        return {
          bgGradient: 'from-slate-900 via-slate-800 to-slate-900',
          textColor: 'text-red-300',
          accentColor: 'text-slate-300',
          label: 'CLIMB TIME — WARNING',
          showResults: false,
          showCurrentClimber: false,
          showNextRound: false,
          showCurrentRoundClimbers: true
        };
      case 'completed':
        return {
          bgGradient: 'from-slate-900 via-slate-800 to-slate-900',
          textColor: 'text-white',
          accentColor: 'text-slate-300',
          label: 'COMPETITION COMPLETED',
          showResults: true,
          showCurrentClimber: false,
          showNextRound: false,
          showCurrentRoundClimbers: false
        };
      case 'paused':
        return {
          bgGradient: 'from-slate-900 via-slate-800 to-slate-900',
          textColor: 'text-white',
          accentColor: 'text-slate-300',
          label: 'COMPETITION PAUSED',
          showResults: true,
          showCurrentClimber: false,
          showNextRound: false,
          showCurrentRoundClimbers: false
        };
      default:
        return {
          bgGradient: 'from-slate-900 via-slate-800 to-slate-900',
          textColor: 'text-white',
          accentColor: 'text-slate-300',
          label: 'READY',
          showResults: false,
          showCurrentClimber: false,
          showNextRound: false,
          showCurrentRoundClimbers: false
        };
    }
  };
  
  const timerInfo = getTimerDisplayInfo();
  const lastResults = getLastRoundResults();
  const nextRoundClimbers = getNextRoundClimbers();
  
  if (!competition) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-white mb-4"></div>
          <h1 className="text-3xl text-white mb-2 font-bold">Loading Competition...</h1>
          <p className="text-slate-400">Please wait while we load the timer.</p>
        </div>
      </div>
    );
  }
  
  return (
    <div 
      className={`min-h-screen bg-gradient-to-br ${timerInfo.bgGradient} flex flex-col p-4 md:p-8 transition-all duration-1000`}
      onClick={handleUserInteraction}
    >
      
      {/* Audio Manager Component */}
      <AudioManager
        timerState={timerState}
        localRemainingTime={localRemainingTime}
        competitionWarningTime={competition?.warningTime}
      />
      
      {/* Audio Enable Prompt */}
      {showAudioPrompt && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 animate-pulse">
          <div className="bg-yellow-600/90 backdrop-blur-sm px-6 py-4 rounded-lg shadow-xl border border-yellow-500/50">
            <div className="flex items-center gap-3">
              <VolumeX className="w-6 h-6 text-white" />
              <div>
                <h3 className="text-white font-bold text-lg">Click anywhere to enable audio</h3>
                <p className="text-yellow-100 text-sm mt-1">
                  Browsers require user interaction to play timer sounds
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Sync Status Indicator */}
      <div className="fixed top-4 right-4 z-50">
        <div className="px-4 py-2 rounded-lg text-sm font-semibold shadow-lg bg-green-600/90 text-white backdrop-blur-sm">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-white"></div>
            SYNCED
          </div>
          {lastSyncTime && (
            <div className="text-xs mt-1 opacity-75">
              Last sync: {new Date(lastSyncTime).toLocaleTimeString()}
            </div>
          )}
        </div>
      </div>
      
      {/* Competition Info */}
      <div className="fixed top-4 left-4 z-50">
        <div className="bg-slate-800/90 backdrop-blur-sm px-4 py-2 rounded-lg">
          <h2 className="text-white font-bold text-lg">{competition.name}</h2>
          <p className="text-slate-300 text-sm">
            Round {timerState?.currentRound || currentRound} of {timerState?.totalRounds || competition.totalRounds || 1}
          </p>
        </div>
      </div>
      
      {/* Timer Label */}
      <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 text-center">
        <div className={`${timerInfo.accentColor} text-1xl md:text-4xl lg:text-3xl tracking-[0.3em] uppercase font-bold`}>
          {timerInfo.label}
        </div>
      </div>
      
      {/* Main Timer Display */}
      <div className="flex-grow flex flex-col justify-center items-center w-full max-w-7xl mx-auto">
        <div className="text-center space-y-4 md:space-y-6 w-full">
          <div className={`${timerInfo.textColor} text-[12rem] sm:text-[16rem] md:text-[22rem] lg:text-[28rem] font-light tracking-tight leading-none ${timerInfo.pulse ? 'animate-pulse' : ''}`}>
            {formatTime(localRemainingTime)}
          </div>
        </div>
      </div>
      
      {/* Current Round Climbers Footer */}
      {timerInfo.showCurrentRoundClimbers && currentRoundClimbers.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-slate-900/80 backdrop-blur-sm border-t border-slate-700/50 py-3 z-40">
          <div className="max-w-8xl mx-auto px-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-bold text-white">
                ROUND {timerState?.currentRound || currentRound} CLIMBERS
              </h3>
              <div className="text-sm text-slate-300">
                {currentRoundClimbers.length} climbers
              </div>
            </div>
            
            {/* Horizontal scrolling container */}
            <div className="relative overflow-hidden">
              <div
                ref={scrollContainerRef}
                className="flex gap-3 py-2"
                style={{
                  maskImage: 'linear-gradient(to right, transparent, black 5%, black 95%, transparent)',
                  WebkitMaskImage: 'linear-gradient(to right, transparent, black 5%, black 95%, transparent)',
                }}
              >
                {[...currentRoundClimbers].map((climber, index) => (
                  <div
                    key={`${climber.bib}-${index}`}
                    className="flex-shrink-0 bg-slate-800/50 backdrop-blur-sm rounded-lg border border-slate-700/50 p-3 w-48"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <div
                            className="w-3 h-3 rounded-full flex-shrink-0"
                            style={{ backgroundColor: climber.boulderColor }}
                          ></div>
                          <span className="text-white font-bold text-sm truncate">{climber.boulder}</span>
                        </div>
                        <div className="text-lg font-semibold text-white truncate">
                          {climber.climber}
                        </div>
                        <div className="text-sm text-slate-300 mt-1">
                          Bib: <span className="text-yellow-300 font-bold">#{climber.bib}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Dual Tables for Wrap Time */}
      {timerInfo.showResults && (
        <div className="mt-auto pt-8 pb-4 max-w-7xl mx-auto w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
            {/* Current Round Results Table */}
            <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg overflow-hidden border border-slate-700">
              <div className="bg-slate-700/80 p-4 border-b border-slate-700">
                <h3 className="text-xl md:text-2xl font-bold text-white text-center">
                  ROUND {timerState?.currentRound || currentRound} RESULTS
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[400px]">
                  <thead>
                    <tr className="border-b border-slate-700 text-slate-300 text-xs md:text-sm">
                      <th className="text-left p-3 md:p-4 font-light">BOULDER</th>
                      <th className="text-left p-3 md:p-4 font-light">CLIMBER</th>
                      <th className="text-center p-3 md:p-4 font-light">TOP</th>
                      <th className="text-center p-3 md:p-4 font-light">ZONE</th>
                      <th className="text-center p-3 md:p-4 font-light">POINTS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lastResults.map((result, index) => (
                      <tr key={index} className="border-b border-slate-700/50 text-white hover:bg-slate-700/30 transition-colors">
                        <td className="p-3 md:p-4 text-lg md:text-xl font-bold">{result.boulder}</td>
                        <td className="p-3 md:p-4 text-sm md:text-base">
                          <span className="text-slate-400">#{result.bib}</span> {result.climber}
                        </td>
                        <td className="text-center p-3 md:p-4">
                          <div className="flex flex-col items-center">
                            {result.topReached ? (
                              <>
                                <CheckCircle2 className="w-5 h-5 text-green-400 mb-1" />
                                <span className="text-sm font-light">{result.attemptsToTop}</span>
                              </>
                            ) : (
                              <span className="text-lg text-slate-600">—</span>
                            )}
                          </div>
                        </td>
                        <td className="text-center p-3 md:p-4">
                          <div className="flex flex-col items-center">
                            {result.zoneReached ? (
                              <>
                                <CheckCircle2 className="w-5 h-5 text-yellow-400 mb-1" />
                                <span className="text-sm font-light">{result.attemptsToZone}</span>
                              </>
                            ) : (
                              <span className="text-lg text-slate-600">—</span>
                            )}
                          </div>
                        </td>
                        <td className="text-center p-3 md:p-4 text-lg md:text-xl font-bold text-green-400">
                          {result.points.toFixed(1)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            
            {/* Next Round Climbers Table */}
            {timerInfo.showNextRound && nextRoundClimbers.length > 0 && (
              <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg overflow-hidden border border-slate-700">
                <div className="bg-slate-700/80 p-4 border-b border-slate-700">
                  <h3 className="text-xl md:text-2xl font-bold text-white text-center">
                    NEXT ROUND CLIMBERS
                  </h3>
                  <p className="text-slate-300 text-center text-sm mt-1">
                    Round {(timerState?.currentRound || currentRound || 1) + 1}
                  </p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[400px]">
                    <thead>
                      <tr className="border-b border-slate-700 text-slate-300 text-xs md:text-sm">
                        <th className="text-left p-3 md:p-4 font-light">BOULDER</th>
                        <th className="text-left p-3 md:p-4 font-light">CLIMBER</th>
                        <th className="text-left p-3 md:p-4 font-light">BIB</th>
                      </tr>
                    </thead>
                    <tbody>
                      {nextRoundClimbers.map((climber, index) => (
                        <tr key={index} className="border-b border-slate-700/50 text-white hover:bg-slate-700/30 transition-colors">
                          <td className="p-3 md:p-4 text-lg md:text-xl font-bold">{climber.boulder}</td>
                          <td className="p-3 md:p-4 text-sm md:text-base">
                            {climber.climber}
                          </td>
                          <td className="p-3 md:p-4 text-sm md:text-base text-slate-400">
                            #{climber.bib}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};